/**
 * Connection status indicator component.
 * Visual indicator for connector connection status.
 */

// React Imports
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';

export type ConnectionStatus = 'Connected' | 'Disconnected' | 'Expired' | 'Error' | 'Unknown';

export interface ConnectionStatusIndicatorProps {
  status: ConnectionStatus;
  message?: string;
  size?: 'small' | 'medium';
}

/**
 * Connection status indicator component.
 */
export const ConnectionStatusIndicator = ({
  status,
  message,
  size = 'small',
}: ConnectionStatusIndicatorProps) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'Connected':
        return {
          color: 'success' as const,
          icon: <CheckCircleIcon fontSize={size} />,
          label: 'Connected',
        };
      case 'Disconnected':
        return {
          color: 'default' as const,
          icon: <HelpOutlineIcon fontSize={size} />,
          label: 'Disconnected',
        };
      case 'Expired':
        return {
          color: 'warning' as const,
          icon: <WarningIcon fontSize={size} />,
          label: 'Expired',
        };
      case 'Error':
        return {
          color: 'error' as const,
          icon: <ErrorIcon fontSize={size} />,
          label: 'Error',
        };
      default:
        return {
          color: 'default' as const,
          icon: <HelpOutlineIcon fontSize={size} />,
          label: 'Unknown',
        };
    }
  };

  const config = getStatusConfig();

  const chip = (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size={size}
      variant="outlined"
      sx={{ fontWeight: 500 }}
    />
  );

  if (message) {
    return (
      <Tooltip title={message} arrow>
        {chip}
      </Tooltip>
    );
  }

  return chip;
};
